import React, { useState, useEffect, useRef } from 'react';
import FormLabel from './FormLabel';
import FormError from './FormError';

const FormInput = ({
    label,
    name,
    type = 'text',
    placeholder,
    value,
    onChange,
    error,
    disabled = false,
    required = false,
    icon,
    endIcon,
    debounce,
    autoFocus = false,
    className = '',
    id: idProp,
}) => {
    const id = idProp || name;
    const [localValue, setLocalValue] = useState(value);
    const debounceRef = useRef(null);

    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    const handleChange = (event) => {
        const next = event.target.value;
        setLocalValue(next);

        if (debounce) {
            if (debounceRef.current) clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => onChange(next), debounce);
        } else {
            onChange(next);
        }
    };

    useEffect(() => () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
    }, []);

    const hasStartIcon = Boolean(icon);
    const hasEndIcon = Boolean(endIcon);
    const wrapClass = [
        'ow-form-input-wrap',
        hasStartIcon && 'ow-form-input-wrap--start-icon',
        hasEndIcon && 'ow-form-input-wrap--end-icon',
        hasStartIcon && hasEndIcon && 'ow-form-input-wrap--both-icons',
    ].filter(Boolean).join(' ');

    const inputClass = [
        'ow-form-input',
        error && 'ow-form-input--error',
        className,
    ].filter(Boolean).join(' ');

    return (
        <div className="ow-form-field">
            {label && (
                <FormLabel text={label} htmlFor={id} required={required} />
            )}
            <div className={wrapClass}>
                {icon && (
                    <span className="ow-form-input-icon ow-form-input-icon--start" aria-hidden="true">
                        {icon}
                    </span>
                )}
                <input
                    id={id}
                    name={name}
                    type={type}
                    className={inputClass}
                    placeholder={placeholder}
                    value={localValue}
                    onChange={handleChange}
                    disabled={disabled}
                    required={required}
                    autoFocus={autoFocus}
                    aria-invalid={Boolean(error)}
                    aria-describedby={error ? `${id}-error` : undefined}
                />
                {endIcon && (
                    <span className="ow-form-input-icon ow-form-input-icon--end">
                        {endIcon}
                    </span>
                )}
            </div>
            {error && <FormError message={error} id={`${id}-error`} />}
        </div>
    );
};

export default FormInput;
